import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { compose } from 'recompose';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import HttpError from '../../components/http-error';
import ActionCard from '../../components/action-card';
import Spinner from '../../components/spinner';
import Pagination, { getCurrentPageNumber } from '../../components/pagination';

import withAPI from '../../hocs/with-api';
import ClientsTableRow from '../../components/clients-table-row';
import Table from '../../components/table';
import FlashMessage from '../../components/flash-message';
import Input from '../../components/input';

const searchInputIconStyle = {
  backgroundImage: "url('/assets/search-icon.png')",
  backgroundSize: '15px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 21px center',
};

class Clients extends Component {
  constructor(props) {
    super(props);
    const { match } = props;
    this.state = {
      clients: [],
      clientName: match?.params?.search
        ? decodeURIComponent(match.params.search)
        : '',
      currentPage: getCurrentPageNumber(props),
      totalPages: 1,
      totalRecords: 1,
      perPage: 1,
      from: 1,
      to: 1,
    };
  }

  async componentDidMount() {
    const { t } = this.props;
    document.title = `${t('clients.all')} - ${t('app.name')}`;
    window.onpopstate = this.onBackButtonClick;
    this.loadClients();
  }

  componentDidUpdate(prevProps) {
    const { location } = this.props;
    const prevPath = prevProps.location.pathname;
    const nextPath = location.pathname;

    if (prevPath !== nextPath && nextPath === '/clients') {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(
        { clientName: '', currentPage: 1 },
        () => this.loadClients(),
      );
    }
  }


  componentWillUnmount() {
    window.onpopstate = null;
  }

  onClientNameChanged = ({ target: { value } }) => {
    this.setState({ clientName: value, currentPage: 1 }, () => {
      this.clientNameChange(value);
    });
  };

  clientNameChange = _.debounce((value) => {
    const { history } = this.props;
    history.push(`/clients/1/${encodeURIComponent(value || '')}`);
    this.loadClients();
  }, 500);

  handlePageChange = (currentPage) => {
    const { history } = this.props;
    const { clientName } = this.state;
    this.setState({
      currentPage,
    }, () => {
      history.push(`/clients/${currentPage}/${encodeURIComponent(clientName)}`);
      this.loadClients();
    });
  }

  onBackButtonClick = () => {
    const page = getCurrentPageNumber(this.props);
    this.setState({
      currentPage: page,
    }, this.loadClients);
  }

  loadClients = async () => {
    const { httpRequest } = this.props;
    const { currentPage, clientName } = this.state;
    const {
      data: {
        data: clients,
        total,
        per_page,
        last_page,
        from,
        to,
      },
    } = await httpRequest({
      method: 'get',
      url: `clients?page=${currentPage}&search=${clientName}`,
    });

    this.setState({
      clients,
      totalPages: last_page,
      perPage: per_page,
      totalRecords: total,
      from,
      to,
    });
  }

  render() {
    const {
      t,
      httpGetDone,
      httpGetLoading,
      httpGetFailed,
    } = this.props;
    const {
      clients,
      from,
      currentPage,
      totalPages,
      to,
      perPage,
      totalRecords,
      clientName,
    } = this.state;
    return (
      <ActionCard title={t('clients.all')} withActionButton={false}>
        <div className="row mb-3">
          <div className="col-xl-6 col-lg-6 col-md-12 mb-2">
            <Input
              name="clientName"
              id="clientName"
              placeholder={t('clients.name')}
              type="text"
              value={clientName}
              style={{ height: '40px', width: '100%', ...searchInputIconStyle }}
              className="input-clean float-right mw-100"
              onChange={this.onClientNameChanged}
            />
          </div>
        </div>
        { httpGetLoading && <Spinner marginTop="555px" /> }
        {
          <FlashMessage />
        }
        {
          httpGetFailed && (
          <HttpError
            withTryAgainButton
            onTryAgain={this.loadClients}
          />
          )
        }
        {
          httpGetDone && !httpGetFailed && !httpGetLoading && (
            <>
              <Table hover responsive="sm" size="sm" isEmpty={_.isEmpty(clients)} emptyMessage={t('messages.noData')}>
                <thead>
                  <tr>
                    <th className="align-middle">{ t('clients.name') }</th>
                    <th className="align-middle hideMobile">{ t('clients.address') }</th>
                    <th className="align-middle actions">{ t('actions') }</th>
                  </tr>
                </thead>
                <tbody>
                  { _.map(clients, (client) => (
                    <ClientsTableRow client={client} key={`client-${client.id}`} />
                  ))}
                </tbody>
              </Table>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onChange={this.handlePageChange}
                totalRecords={totalRecords}
                perPage={perPage}
                from={from}
                to={to}
              />
            </>
          )
        }
      </ActionCard>
    );
  }
}

Clients.propTypes = {
  history: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  httpRequest: PropTypes.func.isRequired,
  httpGetDone: PropTypes.bool.isRequired,
  httpGetLoading: PropTypes.bool,
  httpGetFailed: PropTypes.bool,
  match: PropTypes.shape({
    params: PropTypes.shape({
      search: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
};

Clients.defaultProps = {
  httpGetLoading: true,
  httpGetFailed: false,
};

export default compose(
  withTranslation(),
  withAPI,
  withRouter,
)(Clients);
